import { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import { formatDate } from '../../utils/format';

// ─── Constants ───────────────────────────────────────────────────────────────
const TYPE_LABELS = {
  overtime:            'Overtime',
  standby:             'Standby',
  toil:                'TOIL',
  call_out:            'Call-Out',
  shift_move:          'Shift Move',
  working_away:        'Working Away',
  desk_move_weekday:   'Desk Move (Weekday)',
  desk_move_weekend:   'Desk Move (Weekend)',
  remote_working:      'Remote Working',
  out_of_hours:        'Out of Hours',
};

const LEAVE_LABELS = {
  annual:    'Annual Leave',
  sick:      'Sick Leave',
  other:     'Other Leave',
  maternity: 'Maternity',
  paternity: 'Paternity',
  unpaid:    'Unpaid Leave',
  toil:      'TOIL',
};

const RATE_MULT = { standard: 1, time_and_half: 1.5, double: 2 };
const STANDBY_RATE = 2.0;
const HOURS_PER_DAY = 7.5;
const FLAT_ALLOWANCE_TYPES = ['desk_move_weekday', 'desk_move_weekend', 'remote_working', 'out_of_hours'];
// Types paid as an uplift on the hourly rate over the hours, rather than an
// overtime multiplier: a moved shift and working away are both 25% (policy 2.b, 2.a.vi).
const UPLIFT_TYPES = { shift_move: 0.25, working_away: 0.25 };

const NAVY   = '0D1D32';
const GOLD   = 'B8966E';
const GREEN  = '065F46';
const AMBER  = '92400E';

function fmtDateTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtCurrency(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}
function fmtHours(h) { return h == null ? '—' : `${parseFloat(h).toFixed(2)}h`; }

function periodLabel(from, to) {
  if (!from || !to) return '';
  const f = new Date(from), t = new Date(to);
  const fm = f.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const tm = t.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${fm} – ${tm}`;
}

// ─── Cost computation ────────────────────────────────────────────────────────
function computeRowCost(row, flatRates) {
  const hours = parseFloat(row.hours) || 0;
  if (row.request_type === 'standby') return { hourly_rate: STANDBY_RATE, cost: hours * STANDBY_RATE };
  if (FLAT_ALLOWANCE_TYPES.includes(row.request_type)) {
    const flat = flatRates[row.request_type] ?? null;
    if (flat == null) return { hourly_rate: null, cost: null };
    return { hourly_rate: null, cost: flat };
  }
  if (!row.day_rate) return { hourly_rate: null, cost: null };
  const hourly = row.day_rate / HOURS_PER_DAY;
  if (UPLIFT_TYPES[row.request_type] != null) {
    return { hourly_rate: Math.round(hourly * 100) / 100, cost: Math.round(hours * hourly * UPLIFT_TYPES[row.request_type] * 100) / 100 };
  }
  if (row.rate_type === 'pending' || !row.rate_type) return { hourly_rate: null, cost: null };
  const mult = RATE_MULT[row.rate_type] || 1;
  return { hourly_rate: Math.round(hourly * 100) / 100, cost: Math.round(hours * hourly * mult * 100) / 100 };
}

// ─── Excel export ─────────────────────────────────────────────────────────────
function exportToExcel(rows, summary, leaveRows, leaveDetail, deskMoveRows, fromDate, toDate) {
  const wb = XLSX.utils.book_new();
  const issuedDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const monthLabel = new Date(fromDate).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  // ── Helper: style a cell ────────────────────────────────────────────────────
  const hex = c => ({ rgb: c });
  const bold = { bold: true };
  const wrap = v => ({ v, t: 's' });
  const num  = v => ({ v: v ?? 0, t: 'n' });
  const curr = v => ({ v: v ?? 0, t: 'n', z: '£#,##0.00' });

  // ── SHEET 1: Cover ──────────────────────────────────────────────────────────
  const coverData = [
    [{ v: 'ALTON VALLEY', t: 's', s: { font: { bold: true, sz: 20, color: hex(NAVY) } } }],
    [{ v: `Payroll Export — ${monthLabel}`, t: 's', s: { font: { sz: 14, color: hex(GOLD) } } }],
    [],
    [{ v: 'Period', t: 's', s: { font: bold } }, { v: periodLabel(fromDate, toDate) }],
    [{ v: 'Date Issued', t: 's', s: { font: bold } }, { v: issuedDate }],
    [{ v: 'Sheets', t: 's', s: { font: bold } }, { v: 'Cover  |  Pay Detail  |  Pay Summary  |  Leave & Sickness  |  Overtime & Standby  |  Desk Moves' }],
    [],
    [{ v: 'CONFIDENTIAL — For payroll use only', t: 's', s: { font: { italic: true, color: hex('999999') } } }],
  ];
  const wsCover = XLSX.utils.aoa_to_sheet(coverData);
  wsCover['!cols'] = [{ wch: 16 }, { wch: 50 }];
  wsCover['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }];
  XLSX.utils.book_append_sheet(wb, wsCover, 'Cover');

  // ── SHEET 2: Detail ─────────────────────────────────────────────────────────
  const detailHdr = ['Person', 'Team', 'Type', 'Date', 'Start', 'End', 'Hours', 'Rate Type', 'Hourly Rate (£)', 'Cost (£)', 'Exported?', 'Notes'];
  const detailRows = rows.map(r => [
    r.person_name,
    r.team_name || '',
    TYPE_LABELS[r.request_type] || r.request_type,
    r.date || r.start_datetime?.substring(0, 10) || '',
    r.start_datetime ? new Date(r.start_datetime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
    r.end_datetime   ? new Date(r.end_datetime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
    parseFloat(r.hours || 0),
    r.rate_type === 'pending' ? 'Not set' : (r.rate_type || ''),
    r.hourly_rate ?? '',
    r.cost ?? '',
    r.payroll_exported_at ? 'Yes' : 'No',
    r.notes || r.reason || '',
  ]);
  const wsDetail = XLSX.utils.aoa_to_sheet([detailHdr, ...detailRows]);
  wsDetail['!cols'] = [22, 16, 24, 12, 8, 8, 8, 14, 14, 12, 10, 24].map(w => ({ wch: w }));
  // Style header row navy
  detailHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (wsDetail[ref]) wsDetail[ref].s = { fill: { fgColor: hex(NAVY) }, font: { bold: true, color: hex('FFFFFF') }, alignment: { horizontal: 'center' } };
  });
  XLSX.utils.book_append_sheet(wb, wsDetail, 'Pay Detail');

  // ── SHEET 3: Summary ────────────────────────────────────────────────────────
  const summaryHdr = ['Person', 'Team', 'Type', 'Entries', 'Total Hours', 'Total Cost (£)', 'Rate Issues?'];
  const summaryRows = summary.map(s => [
    s.person_name,
    s.team_name || '',
    TYPE_LABELS[s.request_type] || s.request_type,
    s.entries,
    parseFloat(s.total_hours.toFixed(2)),
    s.has_missing_rate ? '' : parseFloat(s.total_cost.toFixed(2)),
    s.has_missing_rate ? 'MISSING RATE' : 'OK',
  ]);
  // Grand total row
  const grandTotal = summary.filter(s => !s.has_missing_rate).reduce((t, s) => t + s.total_cost, 0);
  summaryRows.push(['', '', 'TOTAL', '', '', parseFloat(grandTotal.toFixed(2)), '']);

  const wsSummary = XLSX.utils.aoa_to_sheet([summaryHdr, ...summaryRows]);
  wsSummary['!cols'] = [22, 16, 24, 8, 12, 16, 14].map(w => ({ wch: w }));
  summaryHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (wsSummary[ref]) wsSummary[ref].s = { fill: { fgColor: hex(NAVY) }, font: { bold: true, color: hex('FFFFFF') }, alignment: { horizontal: 'center' } };
  });
  // Bold total row
  const totalRowIdx = summaryRows.length; // 0-indexed header + data
  summaryHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: totalRowIdx, c: ci });
    if (wsSummary[ref]) wsSummary[ref].s = { font: { bold: true }, fill: { fgColor: hex('F3F4F6') } };
  });
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Pay Summary');

  // ── SHEET 4: Leave & Sickness ────────────────────────────────────────────────
  // Use individual records (leaveDetail) if available, else fall back to aggregated
  const useDetail = leaveDetail && leaveDetail.length > 0;
  const leaveHdr = useDetail
    ? ['Person', 'Team', 'Leave Type', 'From', 'To', 'Days']
    : ['Person', 'Team', 'Leave Type', 'Days in Period'];
  const leaveDataRows = useDetail
    ? leaveDetail.map(r => [
        r.person_name,
        r.team_name || '',
        LEAVE_LABELS[r.leave_type] || r.leave_type,
        r.start_date || '',
        r.end_date || '',
        parseFloat(r.days || 0),
      ])
    : leaveRows.map(r => [
        r.person_name,
        r.team_name || '',
        LEAVE_LABELS[r.leave_type] || r.leave_type,
        parseFloat(r.total_days || 0),
      ]);
  if (leaveDataRows.length === 0) leaveDataRows.push(['No approved leave found for this period', '', '', '', '', '', '']);

  // Per-person leave summary appended below
  const leavePersonMap = leaveRows.reduce((acc, r) => {
    if (!acc[r.person_name]) acc[r.person_name] = { team: r.team_name || '', sick: 0, annual: 0, other: 0 };
    if (r.leave_type === 'sick') acc[r.person_name].sick += parseFloat(r.total_days || 0);
    else if (r.leave_type === 'annual') acc[r.person_name].annual += parseFloat(r.total_days || 0);
    else acc[r.person_name].other += parseFloat(r.total_days || 0);
    return acc;
  }, {});
  const leaveSummaryHdr = ['Person', 'Team', 'Sick Days', 'Annual Leave Days', 'Other Days', 'Total Days'];
  const leaveSummaryRows = Object.entries(leavePersonMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, d]) => [name, d.team, d.sick, d.annual, d.other, d.sick + d.annual + d.other]);
  const totalSick   = leaveSummaryRows.reduce((t, r) => t + r[2], 0);
  const totalAnnual = leaveSummaryRows.reduce((t, r) => t + r[3], 0);
  const totalOther  = leaveSummaryRows.reduce((t, r) => t + r[4], 0);
  leaveSummaryRows.push(['', '', totalSick, totalAnnual, totalOther, totalSick + totalAnnual + totalOther]);

  const wsLeave = XLSX.utils.aoa_to_sheet([
    leaveHdr,
    ...leaveDataRows,
    [],
    leaveSummaryHdr,
    ...leaveSummaryRows,
  ]);
  const leaveCols = useDetail ? [22, 16, 20, 12, 12, 8] : [22, 16, 20, 14];
  wsLeave['!cols'] = leaveCols.map(w => ({ wch: w }));
  leaveHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (wsLeave[ref]) wsLeave[ref].s = { fill: { fgColor: hex(NAVY) }, font: { bold: true, color: hex('FFFFFF') }, alignment: { horizontal: 'center' } };
  });
  // Style summary header (gold)
  const leaveSummaryHdrRow = leaveDataRows.length + 2;
  leaveSummaryHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: leaveSummaryHdrRow, c: ci });
    if (wsLeave[ref]) wsLeave[ref].s = { fill: { fgColor: hex(GOLD) }, font: { bold: true, color: hex('FFFFFF') } };
  });
  // Bold total row
  const leaveTotalRow = leaveSummaryHdrRow + leaveSummaryRows.length;
  leaveSummaryHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: leaveTotalRow, c: ci });
    if (wsLeave[ref]) wsLeave[ref].s = { font: { bold: true }, fill: { fgColor: hex('F3F4F6') } };
  });
  XLSX.utils.book_append_sheet(wb, wsLeave, 'Leave & Sickness');

  // ── SHEET 5: Overtime & Standby ────────────────────────────────────────────
  const otTypes = ['overtime', 'standby', 'call_out', 'toil', 'out_of_hours'];
  const otRows = rows.filter(r => otTypes.includes(r.request_type));
  const otHdr = ['Person', 'Team', 'Type', 'Date', 'Start', 'End', 'Hours', 'Rate Type', 'Hourly Rate (£)', 'Cost (£)', 'Notes'];
  const otDataRows = otRows.map(r => [
    r.person_name,
    r.team_name || '',
    TYPE_LABELS[r.request_type] || r.request_type,
    r.date || r.start_datetime?.substring(0, 10) || '',
    r.start_datetime ? new Date(r.start_datetime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
    r.end_datetime   ? new Date(r.end_datetime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
    parseFloat(r.hours || 0),
    r.rate_type === 'pending' ? 'Not set' : (r.rate_type || ''),
    r.hourly_rate ?? '',
    r.cost ?? '',
    r.notes || r.reason || '',
  ]);
  if (otDataRows.length === 0) otDataRows.push(['No overtime / standby records for this period', '', '', '', '', '', '', '', '', '', '']);

  // Per-person OT summary
  const otPersonMap = {};
  for (const r of otRows) {
    if (!otPersonMap[r.person_name]) otPersonMap[r.person_name] = { team: r.team_name || '', hours: 0, cost: 0 };
    otPersonMap[r.person_name].hours += parseFloat(r.hours || 0);
    if (r.cost != null) otPersonMap[r.person_name].cost += r.cost;
  }
  const otSummaryHdr = ['Person', 'Team', 'Total Hours', 'Total Cost (£)'];
  const otSummaryRows = Object.entries(otPersonMap).sort(([a], [b]) => a.localeCompare(b)).map(([name, d]) => [name, d.team, parseFloat(d.hours.toFixed(2)), parseFloat(d.cost.toFixed(2))]);
  const otTotalHours = otSummaryRows.reduce((t, r) => t + r[2], 0);
  const otTotalCost  = otSummaryRows.reduce((t, r) => t + r[3], 0);
  otSummaryRows.push(['', '', parseFloat(otTotalHours.toFixed(2)), parseFloat(otTotalCost.toFixed(2))]);

  const wsOT = XLSX.utils.aoa_to_sheet([
    otHdr,
    ...otDataRows,
    [],
    otSummaryHdr,
    ...otSummaryRows,
  ]);
  wsOT['!cols'] = [22, 16, 22, 12, 8, 8, 8, 14, 14, 12, 24].map(w => ({ wch: w }));
  otHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (wsOT[ref]) wsOT[ref].s = { fill: { fgColor: hex(NAVY) }, font: { bold: true, color: hex('FFFFFF') }, alignment: { horizontal: 'center' } };
  });
  const otSummaryHdrRow = otDataRows.length + 2;
  otSummaryHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: otSummaryHdrRow, c: ci });
    if (wsOT[ref]) wsOT[ref].s = { fill: { fgColor: hex(GOLD) }, font: { bold: true, color: hex('FFFFFF') } };
  });
  const otTotalRowIdx = otSummaryHdrRow + otSummaryRows.length;
  otSummaryHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: otTotalRowIdx, c: ci });
    if (wsOT[ref]) wsOT[ref].s = { font: { bold: true }, fill: { fgColor: hex('F3F4F6') } };
  });
  XLSX.utils.book_append_sheet(wb, wsOT, 'Overtime & Standby');

  // ── SHEET 5: Desk Moves ──────────────────────────────────────────────────────
  const WKDAY_RATE = 15;
  const WKEND_RATE = 20;
  const dmHdr = ['Date', 'Day', 'Person', 'Team', 'Weekday / Weekend', 'Allowance (£)'];
  const dmDataRows = deskMoveRows.map(r => {
    const d = new Date(r.assignment_date + 'T12:00:00');
    const dow = d.toLocaleDateString('en-GB', { weekday: 'long' });
    const isWkend = d.getDay() === 0 || d.getDay() === 6;
    return [
      d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      dow,
      r.person_name,
      r.team_name || '',
      isWkend ? 'Weekend' : 'Weekday',
      isWkend ? WKEND_RATE : WKDAY_RATE,
    ];
  });
  if (dmDataRows.length === 0) dmDataRows.push(['No desk move assignments for this period', '', '', '', '', '']);
  // Summary by person
  const dmByPerson = deskMoveRows.reduce((acc, r) => {
    if (!acc[r.person_id]) acc[r.person_id] = { name: r.person_name, team: r.team_name || '', weekday: 0, weekend: 0, cost: 0 };
    const d = new Date(r.assignment_date + 'T12:00:00');
    if (d.getDay() === 0 || d.getDay() === 6) { acc[r.person_id].weekend++; acc[r.person_id].cost += WKEND_RATE; }
    else { acc[r.person_id].weekday++; acc[r.person_id].cost += WKDAY_RATE; }
    return acc;
  }, {});
  const dmSummaryRows = Object.values(dmByPerson).sort((a, b) => a.name.localeCompare(b.name)).map(p => [p.name, p.team, p.weekday, p.weekend, p.weekday + p.weekend, p.cost]);
  const dmTotalCost = Object.values(dmByPerson).reduce((t, p) => t + p.cost, 0);

  const wsDM = XLSX.utils.aoa_to_sheet([
    dmHdr,
    ...dmDataRows,
    [],
    ['Person Summary', 'Team', 'Weekday Days', 'Weekend Days', 'Total Days', 'Total Allowance (£)'],
    ...dmSummaryRows,
    ['', '', '', '', 'TOTAL', dmTotalCost],
  ]);
  wsDM['!cols'] = [14, 12, 22, 16, 16, 14].map(w => ({ wch: w }));
  dmHdr.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (wsDM[ref]) wsDM[ref].s = { fill: { fgColor: hex(NAVY) }, font: { bold: true, color: hex('FFFFFF') }, alignment: { horizontal: 'center' } };
  });
  const summaryHdrRow = dmDataRows.length + 2;
  ['Person Summary', 'Team', 'Weekday Days', 'Weekend Days', 'Total Days', 'Total Allowance (£)'].forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: summaryHdrRow, c: ci });
    if (wsDM[ref]) wsDM[ref].s = { fill: { fgColor: hex(GOLD) }, font: { bold: true, color: hex('FFFFFF') } };
  });
  XLSX.utils.book_append_sheet(wb, wsDM, 'Desk Moves');

  const safeMonth = monthLabel.replace(/\s+/g, '_');
  XLSX.writeFile(wb, `ATV_Payroll_${safeMonth}.xlsx`);
  return true;
}

// ─── PayrollPage ──────────────────────────────────────────────────────────────
export default function PayrollPage() {
  const { toast } = useToast();
  const today = new Date();
  const defaultFrom = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const defaultTo = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const [fromDate,    setFromDate]    = useState(defaultFrom);
  const [toDate,      setToDate]      = useState(defaultTo);
  const [typeFilter,  setTypeFilter]  = useState('');
  const [showExported, setShowExported] = useState(false);
  const [rows,        setRows]        = useState([]);
  const [summary,     setSummary]     = useState([]);
  const [leaveRows,   setLeaveRows]   = useState([]);
  const [leaveDetail, setLeaveDetail] = useState([]);
  const [deskMoveRows, setDeskMoveRows] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [exporting,   setExporting]   = useState(false);
  const [marking,     setMarking]     = useState(false);
  const [error,       setError]       = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeTab,   setActiveTab]   = useState('pay'); // pay | leave

  const flatRates = {
    desk_move_weekday: 15,
    desk_move_weekend: 20,
    remote_working: null,
    out_of_hours: null,
  };

  const load = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true); setError(null);
    try {
      const params = { from: fromDate, to: toDate };
      if (typeFilter) params.type = typeFilter;
      // Desk moves are allowance records (desk_move_weekday / desk_move_weekend)
      // raised by the PLOW when a move job is assigned, so they come from the
      // same report as everything else, unfiltered by type.
      const [data, dmReport] = await Promise.all([
        api.get('overtime-requests/report', params),
        typeFilter ? api.get('overtime-requests/report', { from: fromDate, to: toDate }) : null,
      ]);
      const dmSource = (dmReport || data).rows || [];
      const dmData = dmSource
        .filter(r => r.request_type === 'desk_move_weekday' || r.request_type === 'desk_move_weekend')
        .map(r => ({ ...r, assignment_date: r.date }));
      const enriched = (data.rows || []).map(r => {
        const { hourly_rate, cost } = computeRowCost(r, flatRates);
        return { ...r, hourly_rate, cost };
      });
      setRows(enriched);
      const summaryMap = {};
      for (const r of enriched) {
        const key = `${r.person_id}_${r.request_type}`;
        if (!summaryMap[key]) summaryMap[key] = {
          person_id: r.person_id, person_name: r.person_name,
          team_name: r.team_name, request_type: r.request_type,
          total_hours: 0, total_cost: 0, entries: 0, has_missing_rate: false,
        };
        summaryMap[key].total_hours += parseFloat(r.hours || 0);
        if (r.cost != null) summaryMap[key].total_cost += r.cost;
        else summaryMap[key].has_missing_rate = true;
        summaryMap[key].entries++;
      }
      setSummary(Object.values(summaryMap).sort((a, b) => a.person_name.localeCompare(b.person_name)));
      setLeaveRows(data.leave || []);
      setLeaveDetail(data.leaveDetail || []);
      setDeskMoveRows(Array.isArray(dmData) ? dmData : []);
      setSelectedIds(new Set());
    } catch (e) {
      setError(e.message || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const visibleRows   = showExported ? rows : rows.filter(r => !r.payroll_exported_at);
  const unexportedRows = rows.filter(r => !r.payroll_exported_at);

  const groupedByPerson = visibleRows.reduce((acc, r) => {
    if (!acc[r.person_id]) acc[r.person_id] = { person_name: r.person_name, team_name: r.team_name, rows: [] };
    acc[r.person_id].rows.push(r);
    return acc;
  }, {});

  // Group leave detail (individual records) by person
  const leaveByPerson = (leaveDetail.length ? leaveDetail : leaveRows).reduce((acc, r) => {
    if (!acc[r.person_id]) acc[r.person_id] = { person_name: r.person_name, team_name: r.team_name, items: [] };
    acc[r.person_id].items.push(r);
    return acc;
  }, {});

  const hasMissingRate = rows.some(r => !r.payroll_exported_at &&
    (r.rate_type === 'pending' || (!FLAT_ALLOWANCE_TYPES.includes(r.request_type) && r.request_type !== 'standby' && r.cost == null)));

  const toggleRow = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    const ids = unexportedRows.map(r => r.id);
    setSelectedIds(selectedIds.size === ids.length ? new Set() : new Set(ids));
  };

  const handleMarkExported = async () => {
    if (!selectedIds.size) return;
    setMarking(true);
    try {
      await api.post('overtime-requests/mark-exported', { ids: Array.from(selectedIds) });
      await load();
    } catch (e) {
      toast.error('Failed: ' + (e.message || 'Unknown error'));
    } finally { setMarking(false); }
  };

  const handleExport = () => {
    setExporting(true);
    try { exportToExcel(visibleRows, summary, leaveRows, leaveDetail, deskMoveRows, fromDate, toDate); }
    catch (e) { toast.error('Export failed: ' + e.message); }
    finally { setExporting(false); }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const s = {
    page: { fontFamily: 'Montserrat, sans-serif', fontSize: 11, color: '#1F2937', padding: '16px 20px' },
    filterBar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16, padding: '10px 14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8 },
    label: { fontSize: 10, fontWeight: 600, color: '#6B7280', marginRight: 4 },
    inp: { border: '1px solid #D1D5DB', borderRadius: 5, padding: '4px 8px', fontSize: 11, fontFamily: 'Montserrat', color: '#1F2937', background: '#fff' },
    btn: (bg, fg = '#fff') => ({ background: bg, color: fg, border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'Montserrat' }),
    pill: (a) => ({ padding: '3px 10px', borderRadius: 12, fontSize: 9, fontWeight: 600, cursor: 'pointer', background: a ? '#0D1D32' : '#F3F4F6', color: a ? '#fff' : '#6B7280', border: a ? 'none' : '1px solid #E5E7EB' }),
    tab: (a) => ({ padding: '5px 16px', fontSize: 10, fontWeight: 600, cursor: 'pointer', color: a ? '#0D1D32' : '#6B7280', background: 'none', border: 'none', borderBottom: a ? '2px solid #0D1D32' : '2px solid transparent', fontFamily: 'Montserrat' }),
    card: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 14, overflow: 'hidden' },
    cardHdr: { padding: '8px 14px', background: '#F3F4F6', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 8 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 10 },
    th: { textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB', background: '#FAFAFA', fontSize: 9 },
    td: { padding: '5px 10px', borderBottom: '1px solid #F3F4F6', color: '#374151', verticalAlign: 'middle' },
    badge: (c) => ({ display: 'inline-block', padding: '2px 7px', borderRadius: 10, fontSize: 9, fontWeight: 600, background: c === 'amber' ? '#FEF3C7' : c === 'green' ? '#D1FAE5' : c === 'blue' ? '#DBEAFE' : '#F3F4F6', color: c === 'amber' ? '#92400E' : c === 'green' ? '#065F46' : c === 'blue' ? '#1E40AF' : '#6B7280' }),
    warn: { background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 14px', marginBottom: 14, fontSize: 10, color: '#92400E', display: 'flex', alignItems: 'center', gap: 8 },
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0D1D32' }}>Payroll Export</div>
          <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 2 }}>{periodLabel(fromDate, toDate)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedIds.size > 0 && (
            <button style={s.btn('#059669')} onClick={handleMarkExported} disabled={marking}>
              {marking ? 'Marking…' : `✓ Mark ${selectedIds.size} as Exported`}
            </button>
          )}
          <button style={s.btn('#1D4ED8')} onClick={handleExport} disabled={exporting || loading}>
            {exporting ? 'Exporting…' : '⬇ Export Excel'}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={s.filterBar}>
        <span style={s.label}>From</span>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={s.inp} />
        <span style={s.label}>To</span>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={s.inp} />
        <span style={{ ...s.label, marginLeft: 8 }}>Type</span>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={s.inp}>
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={s.label}>Show exported</span>
          <button style={s.pill(showExported)} onClick={() => setShowExported(p => !p)}>{showExported ? 'Yes' : 'No'}</button>
        </div>
      </div>

      {hasMissingRate && (
        <div style={s.warn}>⚠️ Some records have no rate set — cost cannot be calculated. Set rates in Approval Requests before exporting.</div>
      )}
      {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 14px', marginBottom: 14, fontSize: 10, color: '#991B1B' }}>{error}</div>}
      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Loading…</div>}

      {/* Tabs */}
      {!loading && (
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #E5E7EB', marginBottom: 14 }}>
          <button style={s.tab(activeTab === 'pay')} onClick={() => setActiveTab('pay')}>
            Pay Items ({rows.length})
          </button>
          <button style={s.tab(activeTab === 'leave')} onClick={() => setActiveTab('leave')}>
            Leave ({leaveDetail.length || leaveRows.length})
          </button>
          <button style={s.tab(activeTab === 'desk-moves')} onClick={() => setActiveTab('desk-moves')}>
            Desk Moves ({deskMoveRows.length})
          </button>
          <button style={s.tab(activeTab === 'summary')} onClick={() => setActiveTab('summary')}>
            Summary
          </button>
        </div>
      )}

      {/* ── PAY ITEMS TAB ── */}
      {!loading && activeTab === 'pay' && (
        <>
          {unexportedRows.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <input type="checkbox" checked={selectedIds.size === unexportedRows.length} onChange={toggleAll} style={{ cursor: 'pointer' }} />
              <span style={{ fontSize: 10, color: '#6B7280' }}>
                {selectedIds.size === 0 ? `Select all ${unexportedRows.length} un-exported` : `${selectedIds.size} selected`}
              </span>
              {rows.length !== unexportedRows.length && <span style={s.badge('blue')}>{rows.length - unexportedRows.length} already exported</span>}
            </div>
          )}

          {Object.values(groupedByPerson).map(group => {
            const total  = group.rows.reduce((s, r) => r.cost != null ? s + r.cost : s, 0);
            const hours  = group.rows.reduce((s, r) => s + parseFloat(r.hours || 0), 0);
            const missing = group.rows.some(r => r.cost == null);
            return (
              <div key={group.rows[0]?.person_id} style={s.card}>
                <div style={s.cardHdr}>
                  <span style={{ fontWeight: 700, fontSize: 11, color: '#0D1D32' }}>{group.person_name}</span>
                  {group.team_name && <span style={{ fontSize: 9, color: '#9CA3AF' }}>{group.team_name}</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6B7280' }}>{hours.toFixed(2)}h</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: missing ? '#B45309' : '#065F46' }}>
                    {missing ? 'Cost incomplete' : fmtCurrency(total)}
                  </span>
                </div>
                <div style={{ overflowX: 'auto' }}><table style={s.table}>
                  <thead><tr>
                    <th style={{ ...s.th, width: 28 }}></th>
                    <th style={s.th}>Type</th><th style={s.th}>Date</th>
                    <th style={s.th}>Start</th><th style={s.th}>End</th>
                    <th style={s.th}>Hours</th><th style={s.th}>Rate</th>
                    <th style={s.th}>Hourly</th><th style={s.th}>Cost</th>
                    <th style={s.th}>Notes</th><th style={s.th}>Exported</th>
                  </tr></thead>
                  <tbody>
                    {group.rows.map(r => {
                      const isPending = r.rate_type === 'pending' || (!r.rate_type && !FLAT_ALLOWANCE_TYPES.includes(r.request_type) && r.request_type !== 'standby');
                      const isExported = !!r.payroll_exported_at;
                      return (
                        <tr key={r.id} style={{ background: isExported ? '#F9FAFB' : '', opacity: isExported ? 0.7 : 1 }}>
                          <td style={s.td}>{!isExported && <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggleRow(r.id)} style={{ cursor: 'pointer' }} />}</td>
                          <td style={s.td}><span style={s.badge('blue')}>{TYPE_LABELS[r.request_type] || r.request_type}</span></td>
                          <td style={s.td}>{formatDate(r.date || r.start_datetime)}</td>
                          <td style={s.td}>{r.start_datetime ? new Date(r.start_datetime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          <td style={s.td}>{r.end_datetime ? new Date(r.end_datetime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          <td style={s.td}>{fmtHours(r.hours)}</td>
                          <td style={s.td}>{isPending ? <span style={s.badge('amber')}>Not set</span> : <span style={{ fontSize: 10 }}>{r.rate_type === 'standard' ? 'Std' : r.rate_type === 'time_and_half' ? '×1.5' : r.rate_type === 'double' ? '×2' : r.rate_type || '—'}</span>}</td>
                          <td style={{ ...s.td, fontVariantNumeric: 'tabular-nums' }}>{r.hourly_rate != null ? `£${r.hourly_rate.toFixed(2)}` : '—'}</td>
                          <td style={{ ...s.td, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: r.cost != null ? '#065F46' : '#B45309' }}>{r.cost != null ? fmtCurrency(r.cost) : '—'}</td>
                          <td style={{ ...s.td, color: '#9CA3AF', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || r.reason || '—'}</td>
                          <td style={s.td}>{isExported ? <span style={{ fontSize: 9, color: '#6B7280' }} title={fmtDateTime(r.payroll_exported_at)}>✓ {formatDate(r.payroll_exported_at)}</span> : <span style={s.badge('gray')}>—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              </div>
            );
          })}
          {visibleRows.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No approved records found for this date range.</div>}
        </>
      )}

      {/* ── LEAVE TAB ── */}
      {!loading && activeTab === 'leave' && (
        <>
          {Object.keys(leaveByPerson).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No approved leave found for this date range.</div>
          ) : Object.values(leaveByPerson).sort((a, b) => a.person_name.localeCompare(b.person_name)).map(group => {
            const hasDetail = leaveDetail.length > 0;
            const totalDays = group.items.reduce((t, r) => t + parseFloat(r.days || r.total_days || 0), 0);
            return (
              <div key={group.items[0]?.person_id} style={s.card}>
                <div style={s.cardHdr}>
                  <span style={{ fontWeight: 700, fontSize: 11, color: '#0D1D32' }}>{group.person_name}</span>
                  {group.team_name && <span style={{ fontSize: 9, color: '#9CA3AF' }}>{group.team_name}</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6B7280' }}>{totalDays.toFixed(1)} days total</span>
                </div>
                <div style={{ overflowX: 'auto' }}><table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Leave Type</th>
                      {hasDetail && <><th style={s.th}>From</th><th style={s.th}>To</th></>}
                      <th style={s.th}>Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((r, i) => (
                      <tr key={i}>
                        <td style={s.td}>
                          <span style={s.badge(r.leave_type === 'sick' ? 'amber' : r.leave_type === 'annual' ? 'blue' : 'gray')}>
                            {LEAVE_LABELS[r.leave_type] || r.leave_type}
                          </span>
                        </td>
                        {hasDetail && <><td style={s.td}>{formatDate(r.start_date)}</td><td style={s.td}>{formatDate(r.end_date)}</td></>}
                        <td style={{ ...s.td, fontWeight: 600 }}>{parseFloat(r.days || r.total_days || 0).toFixed(1)} days</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            );
          })}
        </>
      )}

      {/* ── DESK MOVES TAB ── */}
      {!loading && activeTab === 'desk-moves' && (() => {
        const WKDAY = 15, WKEND = 20;
        const isWkend = d => { const day = new Date(d + 'T12:00:00').getDay(); return day === 0 || day === 6; };
        // Group by person
        const byPerson = deskMoveRows.reduce((acc, r) => {
          if (!acc[r.person_id]) acc[r.person_id] = { person_name: r.person_name, team_name: r.team_name, rows: [] };
          acc[r.person_id].rows.push(r);
          return acc;
        }, {});
        const dmTotal = deskMoveRows.reduce((t, r) => t + (isWkend(r.assignment_date) ? WKEND : WKDAY), 0);
        return (
          <>
            {/* Stats bar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
              {[
                ['Assignments', deskMoveRows.length, '#EFF6FF', '#1D4ED8'],
                ['Weekday', deskMoveRows.filter(r => !isWkend(r.assignment_date)).length, '#F0FDF4', '#065F46'],
                ['Weekend', deskMoveRows.filter(r =>  isWkend(r.assignment_date)).length, '#FEF3C7', '#92400E'],
                ['Est. Total', `£${dmTotal}`, '#F5F3FF', '#5B21B6'],
              ].map(([label, val, bg, col]) => (
                <div key={label} style={{ background: bg, borderRadius: 8, padding: '8px 12px', border: `1px solid ${bg}` }}>
                  <div style={{ fontSize: 7, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: col }}>{val}</div>
                </div>
              ))}
            </div>
            {deskMoveRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No desk move assignments for this period.</div>
            ) : (
              <>
                {Object.values(byPerson).sort((a,b) => a.person_name.localeCompare(b.person_name)).map(group => {
                  const wkday = group.rows.filter(r => !isWkend(r.assignment_date)).length;
                  const wkend = group.rows.filter(r =>  isWkend(r.assignment_date)).length;
                  const cost  = wkday * WKDAY + wkend * WKEND;
                  return (
                    <div key={group.rows[0].person_id} style={s.card}>
                      <div style={s.cardHdr}>
                        <span style={{ fontWeight: 700, fontSize: 11, color: '#0D1D32' }}>{group.person_name}</span>
                        {group.team_name && <span style={{ fontSize: 9, color: '#9CA3AF' }}>{group.team_name}</span>}
                        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: '#065F46' }}>£{cost}</span>
                      </div>
                      <div style={{ overflowX: 'auto' }}><table style={s.table}>
                        <thead><tr>
                          <th style={s.th}>Date</th>
                          <th style={s.th}>Day</th>
                          <th style={s.th}>Type</th>
                          <th style={s.th}>Allowance</th>
                        </tr></thead>
                        <tbody>
                          {group.rows.sort((a,b) => a.assignment_date.localeCompare(b.assignment_date)).map(r => {
                            const weekend = isWkend(r.assignment_date);
                            const d = new Date(r.assignment_date + 'T12:00:00');
                            return (
                              <tr key={r.id}>
                                <td style={s.td}>{d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                <td style={s.td}>{d.toLocaleDateString('en-GB', { weekday: 'long' })}</td>
                                <td style={s.td}><span style={s.badge(weekend ? 'amber' : 'blue')}>{weekend ? 'Weekend' : 'Weekday'}</span></td>
                                <td style={{ ...s.td, fontWeight: 600, color: '#065F46' }}>£{weekend ? WKEND : WKDAY}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table></div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        );
      })()}

      {/* ── SUMMARY TAB ── */}
      {!loading && activeTab === 'summary' && (
        <>
          <div style={{ ...s.card, marginBottom: 20 }}>
            <div style={s.cardHdr}><span style={{ fontWeight: 700, color: '#0D1D32' }}>Pay Items by Person &amp; Type</span></div>
            <div style={{ overflowX: 'auto' }}><table style={s.table}>
              <thead><tr>
                <th style={s.th}>Person</th><th style={s.th}>Team</th><th style={s.th}>Type</th>
                <th style={s.th}>Entries</th><th style={s.th}>Total Hours</th>
                <th style={s.th}>Total Cost</th><th style={s.th}>Status</th>
              </tr></thead>
              <tbody>
                {summary.map((s2, i) => (
                  <tr key={i}>
                    <td style={s.td}><strong>{s2.person_name}</strong></td>
                    <td style={s.td}>{s2.team_name || '—'}</td>
                    <td style={s.td}><span style={s.badge('blue')}>{TYPE_LABELS[s2.request_type] || s2.request_type}</span></td>
                    <td style={s.td}>{s2.entries}</td>
                    <td style={{ ...s.td, fontVariantNumeric: 'tabular-nums' }}>{s2.total_hours.toFixed(2)}h</td>
                    <td style={{ ...s.td, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: s2.total_cost ? '#065F46' : '#B45309' }}>
                      {s2.has_missing_rate ? <span style={s.badge('amber')}>Incomplete</span> : fmtCurrency(s2.total_cost)}
                    </td>
                    <td style={s.td}>{s2.has_missing_rate ? <span style={s.badge('amber')}>⚠ Missing</span> : <span style={s.badge('green')}>✓ OK</span>}</td>
                  </tr>
                ))}
                {summary.length > 0 && (
                  <tr style={{ background: '#F9FAFB' }}>
                    <td style={{ ...s.td, fontWeight: 700 }} colSpan={4}>Grand Total</td>
                    <td style={{ ...s.td, fontWeight: 700 }}>{summary.reduce((t, s2) => t + s2.total_hours, 0).toFixed(2)}h</td>
                    <td style={{ ...s.td, fontWeight: 700, color: '#065F46' }}>{fmtCurrency(summary.filter(s2 => !s2.has_missing_rate).reduce((t, s2) => t + s2.total_cost, 0))}</td>
                    <td style={s.td}></td>
                  </tr>
                )}
              </tbody>
            </table></div>
          </div>

          {leaveRows.length > 0 && (
            <div style={s.card}>
              <div style={s.cardHdr}><span style={{ fontWeight: 700, color: '#0D1D32' }}>Leave &amp; Sickness Summary</span></div>
              <div style={{ overflowX: 'auto' }}><table style={s.table}>
                <thead><tr><th style={s.th}>Person</th><th style={s.th}>Team</th><th style={s.th}>Leave Type</th><th style={s.th}>Days</th></tr></thead>
                <tbody>
                  {leaveRows.map((r, i) => (
                    <tr key={i}>
                      <td style={s.td}><strong>{r.person_name}</strong></td>
                      <td style={s.td}>{r.team_name || '—'}</td>
                      <td style={s.td}><span style={s.badge(r.leave_type === 'sick' ? 'amber' : r.leave_type === 'annual' ? 'blue' : 'gray')}>{LEAVE_LABELS[r.leave_type] || r.leave_type}</span></td>
                      <td style={{ ...s.td, fontWeight: 600 }}>{parseFloat(r.total_days || 0).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          {deskMoveRows.length > 0 && (() => {
            const WKDAY = 15, WKEND = 20;
            const isWkend = d => { const day = new Date(d + 'T12:00:00').getDay(); return day === 0 || day === 6; };
            const dmByPerson = deskMoveRows.reduce((acc, r) => {
              if (!acc[r.person_id]) acc[r.person_id] = { person_name: r.person_name, team_name: r.team_name, weekday: 0, weekend: 0, cost: 0 };
              if (isWkend(r.assignment_date)) { acc[r.person_id].weekend++; acc[r.person_id].cost += WKEND; }
              else { acc[r.person_id].weekday++; acc[r.person_id].cost += WKDAY; }
              return acc;
            }, {});
            const totalDmCost = Object.values(dmByPerson).reduce((t, p) => t + p.cost, 0);
            return (
              <div style={s.card}>
                <div style={s.cardHdr}>
                  <span style={{ fontWeight: 700, color: '#0D1D32' }}>Desk Move Allowances</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#065F46', fontSize: 10 }}>{fmtCurrency(totalDmCost)} total</span>
                </div>
                <div style={{ overflowX: 'auto' }}><table style={s.table}>
                  <thead><tr>
                    <th style={s.th}>Person</th><th style={s.th}>Team</th>
                    <th style={s.th}>Weekday Days</th><th style={s.th}>Weekend Days</th>
                    <th style={s.th}>Total Days</th><th style={s.th}>Allowance</th>
                  </tr></thead>
                  <tbody>
                    {Object.values(dmByPerson).sort((a, b) => a.person_name.localeCompare(b.person_name)).map((p, i) => (
                      <tr key={i}>
                        <td style={s.td}><strong>{p.person_name}</strong></td>
                        <td style={s.td}>{p.team_name || '—'}</td>
                        <td style={s.td}>{p.weekday}</td>
                        <td style={s.td}>{p.weekend}</td>
                        <td style={{ ...s.td, fontWeight: 600 }}>{p.weekday + p.weekend}</td>
                        <td style={{ ...s.td, fontWeight: 700, color: '#065F46' }}>{fmtCurrency(p.cost)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#F9FAFB' }}>
                      <td style={{ ...s.td, fontWeight: 700 }} colSpan={4}>Total</td>
                      <td style={{ ...s.td, fontWeight: 700 }}>{deskMoveRows.length}</td>
                      <td style={{ ...s.td, fontWeight: 700, color: '#065F46' }}>{fmtCurrency(totalDmCost)}</td>
                    </tr>
                  </tbody>
                </table></div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
